import type { PlanBeat, PlanCue, PlanEffect, PlanEffectEnvelope, ScenePlan } from '@toolproof-core/lib/_contract/scene-plan';
import type { SceneSubtitleTrace, SpaceOverlayIntent, SpaceOverlaySection } from '@toolproof-core/visualization/runtime/_contract/overlay';

import { parsePlanPresentation, parsePointerPlacement, parsePresentationEffect, parseTextPanel } from '@/_pure/planPresentation';
import { splitSubtitleSentences } from '@/_pure/narrationTimeline';
import { parseEpisodeComments } from './episodeComments';
import type { EpisodeCommented } from '@/_contract/episodeComments';
import { normalizeAuthoredBeat, parseNarrationPauses, parseNarrationTextMap } from './episodeAuthoring';
import type { ConceptBeat, ConceptCue, ConceptPresentation, EpisodePlayback, SubtitleTrace } from '@/_contract/episode';
import type { TraceReference, TraceReplay } from '@/_contract/trace';

function isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function episodeBeatSection(
    scenes: readonly { title: string; beats: readonly Pick<PlanBeat, 'id'>[] }[],
    sceneIndex: number, beatIndex: number,
): SpaceOverlaySection {
    return { sectionKind: 'select', sectionId: 'episode-beat', label: 'Beat',
        selectedValue: `${sceneIndex}:${beatIndex}`, allowDuringPlayback: true,
        options: scenes.flatMap((scene, sceneIndex) => scene.beats.map((beat, beatIndex) => ({
            value: `${sceneIndex}:${beatIndex}`,
            label: `${sceneIndex + 1}. ${scene.title} / ${beatIndex + 1}. ${beat.id}`,
        }))) };
}

export function resolveEpisodeBeatSelection(scenes: readonly { beats: readonly unknown[] }[], value: string) {
    if (!/^\d+:\d+$/.test(value)) throw new Error('Invalid episode beat selection.');
    const [sceneIndex, beatIndex] = value.split(':').map(Number);
    if (sceneIndex === undefined || beatIndex === undefined || !scenes[sceneIndex]?.beats[beatIndex]) {
        throw new Error('Episode beat selection is out of range.');
    }
    return { sceneIndex, beatIndex };
}

/** Subtitles show the current narration sentence: always without sound, optionally with it. */
export function withSceneSubtitle(playback: EpisodePlayback | undefined,
    beat: Pick<PlanBeat, 'explanation' | 'spokenText' | 'narrationTextMap'> | undefined): { sceneSubtitle?: string; sceneSubtitleTrace?: SceneSubtitleTrace } {
    if (!playback || !beat || playback.error
        || (playback.soundEnabled && !playback.subtitlesEnabled)) return {};
    const index = playback.subtitleIndex ?? (playback.status !== 'moving'
        && (playback.isPlaying || playback.narrationRequested) ? 0 : undefined);
    if (index === undefined) return {};
    const sentence = splitSubtitleSentences(beat.narrationTextMap ? beat.explanation : beat.spokenText ?? beat.explanation)[index];
    const trace = playback.subtitleTrace ?? 'brighten';
    return sentence ? { sceneSubtitle: sentence.text, ...(trace === 'off' ? {} : { sceneSubtitleTrace: trace }) } : {};
}

const SUBTITLE_TRACES: readonly { value: SubtitleTrace; label: string }[] = [
    { value: 'brighten', label: 'Brighten spoken words' },
    { value: 'underline', label: 'Underline spoken words' },
    { value: 'off', label: 'Off' },
];

/** Sound, then the subtitle choices that apply: Subtitles only with sound, Word trace whenever subtitles show. */
export function episodeSubtitleSections(playback: EpisodePlayback): SpaceOverlaySection[] {
    const subtitlesShown = !playback.soundEnabled || playback.subtitlesEnabled;
    return [
        { sectionKind: 'checkbox', sectionId: 'episode-sound', label: 'Sound',
            checked: playback.soundEnabled, description: 'AI-generated voice', allowDuringPlayback: true },
        ...(playback.soundEnabled ? [{ sectionKind: 'checkbox' as const, sectionId: 'episode-subtitles', label: 'Subtitles',
            checked: playback.subtitlesEnabled, allowDuringPlayback: true }] : []),
        ...(subtitlesShown ? [{ sectionKind: 'select' as const, sectionId: 'episode-subtitle-trace', label: 'Word trace',
            selectedValue: playback.subtitleTrace ?? 'brighten', allowDuringPlayback: true,
            options: SUBTITLE_TRACES.map(({ value, label }) => ({ value, label })) }] : []),
    ];
}

/** The subtitle preferences; undefined when the intent is not one of them. */
export function reduceEpisodeSubtitleIntent(playback: EpisodePlayback, intent: SpaceOverlayIntent): EpisodePlayback | undefined {
    if (intent.overlayIntentKind === 'checkbox-changed' && intent.sectionId === 'episode-subtitles') {
        return { ...playback, subtitlesEnabled: intent.checked };
    }
    if (intent.overlayIntentKind === 'select-changed' && intent.sectionId === 'episode-subtitle-trace') {
        const trace = SUBTITLE_TRACES.find(({ value }) => value === intent.value)?.value;
        return trace ? { ...playback, subtitleTrace: trace } : playback;
    }
    return undefined;
}

export function isSameEpisodePlaybackExecution(current: EpisodePlayback | undefined, expected: EpisodePlayback): boolean {
    return current?.episodeHandle === expected.episodeHandle
        && current.beatIndex === expected.beatIndex && current.revision === expected.revision;
}

function requireText(value: unknown, field: string): string {
    if (typeof value !== 'string' || value.trim() === '') throw new Error(`ScenePlan ${field} must be a nonempty string.`);
    return value;
}

function requireKnownFields(record: Record<string, unknown>, fields: readonly string[], context: string): void {
    for (const field of Object.keys(record)) {
        if (!fields.includes(field)) throw new Error(`Unknown ${context} field '${field}'.`);
    }
}

export function parseTraceReference(trace: unknown): TraceReference {
    if (!isRecord(trace) || !Number.isInteger(trace.schemaVersion) || Number(trace.schemaVersion) < 1) {
        throw new Error('ScenePlan requires a versioned trace reference.');
    }
    requireKnownFields(trace, ['adapter', 'traceKind', 'handle', 'schemaVersion'], 'ScenePlan trace');
    return {
        adapter: requireText(trace.adapter, 'trace.adapter'),
        traceKind: requireText(trace.traceKind, 'trace.traceKind'),
        handle: requireText(trace.handle, 'trace.handle'),
        schemaVersion: Number(trace.schemaVersion),
    };
}

/** Strict ScenePlan parser with authoring-oriented errors; the lib schema parser remains the transport check. */
export function parseTraceScenePlan<TEffect extends PlanEffectEnvelope = PlanEffect>(
    payload: unknown, parseEffects?: (effects: readonly unknown[]) => readonly TEffect[],
): Omit<ScenePlan<TEffect>, 'beats'> & { beats: readonly (PlanBeat<TEffect> & EpisodeCommented)[] } {
    if (!isRecord(payload) || payload.schemaVersion !== 1) throw new Error('Unsupported ScenePlan schema version.');
    requireKnownFields(payload, ['schemaVersion', 'title', 'trace', 'beats'], 'ScenePlan');
    const reference = parseTraceReference(payload.trace);
    if (!Array.isArray(payload.beats) || payload.beats.length === 0) throw new Error('ScenePlan requires at least one beat.');
    const ids = new Set<string>();
    const beats: (PlanBeat<TEffect> & EpisodeCommented)[] = payload.beats.map((value: unknown) => {
        const beat = normalizeAuthoredBeat(value, 'trace');
        if (!isRecord(beat)) throw new Error('Invalid ScenePlan beat.');
        requireKnownFields(beat, ['id', 'comments', 'landmark', 'replayFrom', 'explanation', 'spokenText', 'narrationTextMap', 'narrationPauses', 'readingHoldMs', 'cues', 'presentation'], 'ScenePlan beat');
        const id = requireText(beat.id, 'beat.id');
        if (ids.has(id)) throw new Error(`Duplicate ScenePlan beat '${id}'.`);
        ids.add(id);
        if (typeof beat.readingHoldMs !== 'number' || !Number.isFinite(beat.readingHoldMs) || beat.readingHoldMs < 0) {
            throw new Error(`Invalid reading hold for ScenePlan beat '${id}'.`);
        }
        let replayFrom: PlanBeat['replayFrom'];
        if (beat.replayFrom !== undefined) {
            if (!isRecord(beat.replayFrom)) throw new Error(`Invalid replayFrom for ScenePlan beat '${id}'.`);
            requireKnownFields(beat.replayFrom, ['landmark', 'phase'], 'ScenePlan replayFrom');
            const phase = beat.replayFrom.phase;
            if (phase !== undefined && phase !== 'start' && phase !== 'complete') {
                throw new Error(`Invalid replayFrom phase for ScenePlan beat '${id}'.`);
            }
            replayFrom = { landmark: requireText(beat.replayFrom.landmark, 'replayFrom.landmark'),
                ...(phase === undefined ? {} : { phase }) };
        }
        return {
            id,
            ...parseEpisodeComments(beat.comments),
            landmark: requireText(beat.landmark, 'beat.landmark'),
            ...(replayFrom === undefined ? {} : { replayFrom }),
            explanation: requireText(beat.explanation, 'beat.explanation'),
            readingHoldMs: beat.readingHoldMs,
            ...(beat.narrationTextMap === undefined ? {} : { narrationTextMap: parseNarrationTextMap(beat.narrationTextMap,
                requireText(beat.spokenText, 'beat.spokenText'), requireText(beat.explanation, 'beat.explanation')) }),
            ...(beat.narrationPauses === undefined ? {} : { narrationPauses: parseNarrationPauses(beat.narrationPauses,
                requireText(beat.spokenText ?? beat.explanation, 'beat narration')) }),
            ...(beat.presentation === undefined ? {} : { presentation: parsePlanPresentation(beat.presentation, beat.cues, parseEffects) }),
            ...(beat.cues === undefined ? {} : { cues: parsePlanCues(beat.cues,
                requireText(beat.spokenText ?? beat.explanation, 'beat narration')) }),
            ...(beat.spokenText === undefined ? {} : { spokenText: requireText(beat.spokenText, 'beat.spokenText') }),
        };
    });
    return { schemaVersion: 1, title: requireText(payload.title, 'title'), trace: reference, beats };
}

export function planBeatOpeningIndex(
    plan: Pick<ScenePlan<PlanEffectEnvelope>, 'beats'>, replay: TraceReplay, beatIndex: number, sceneOpening: number,
): number {
    const beat = plan.beats[beatIndex];
    if (!beat) throw new Error('ScenePlan requires an existing beat.');
    const opening = beat.replayFrom ? planAnchorIndex(replay, beat.replayFrom)
        : beatIndex === 0 ? sceneOpening : planAnchorIndex(replay, plan.beats[beatIndex - 1]!);
    if (opening < 0) throw new Error('ScenePlan requires an exact opening checkpoint.');
    return opening;
}

export function validateScenePlanReplay<TEffect extends PlanEffectEnvelope>(plan: ScenePlan<TEffect>, replay: TraceReplay): void {
    if (plan.trace.adapter !== replay.trace.adapter || plan.trace.traceKind !== replay.trace.traceKind
        || plan.trace.handle !== replay.trace.handle || plan.trace.schemaVersion !== replay.trace.schemaVersion) {
        throw new Error(`ScenePlan '${plan.title}' does not match its replay trace.`);
    }
    const landmarks = new Set(replay.landmarks.map((landmark) => landmark.id));
    let previousDestination = -1;
    for (const [beatIndex, beat] of plan.beats.entries()) {
        if (!landmarks.has(beat.landmark)) throw new Error(`ScenePlan beat '${beat.id}' references missing landmark '${beat.landmark}'.`);
        const destination = replay.landmarks.findIndex((entry) => entry.id === beat.landmark);
        if (destination < previousDestination) throw new Error(`Unordered ScenePlan beat '${beat.id}'.`);
        const opening = beat.replayFrom ? planAnchorIndex(replay, beat.replayFrom) : previousDestination;
        if (beat.replayFrom && (beatIndex === 0 || opening < 0 || opening >= destination || destination !== previousDestination
            || !beat.cues?.length || planAnchorIndex(replay, beat.cues[0]!) !== opening)) {
            throw new Error(`Replay beat '${beat.id}' must rewind and return to the preceding endpoint.`);
        }
        let previous = opening;
        for (const cue of beat.cues ?? []) {
            const index = planAnchorIndex(replay, cue);
            if (index < previous || index < 0 || index > destination) {
                throw new Error(`ScenePlan cue '${cue.id}' requires an ordered landmark no later than its beat destination.`);
            }
            previous = index;
        }
        for (const segment of beat.presentation ?? []) {
            if (!segment.until) continue;
            const index = planAnchorIndex(replay, segment.until);
            const sourceCue = segment.afterCue ? beat.cues?.find(cue => cue.id === segment.afterCue) : undefined;
            if (segment.afterCue && !sourceCue) throw new Error(`Missing presentation source cue '${segment.afterCue}'.`);
            const from = sourceCue ? planAnchorIndex(replay, sourceCue) : opening;
            if (index < from || index > destination) throw new Error(`Invalid presentation end anchor '${segment.id}'.`);
        }
        previousDestination = destination;
    }
}

function parseSpokenCues(value: unknown, text: string, fields: readonly string[]) {
    if (!Array.isArray(value) || !value.length) throw new Error('ScenePlan cues must be a nonempty array.');
    let previousOffset = -1;
    const ids = new Set<string>();
    return value.map((raw: unknown) => {
        if (!isRecord(raw)) throw new Error('Invalid ScenePlan cue.');
        requireKnownFields(raw, fields, 'ScenePlan cue');
        const id = requireText(raw.id, 'cue.id');
        const beforeText = requireText(raw.beforeText, 'cue.beforeText');
        const offset = text.indexOf(beforeText);
        if (!/^[a-z][a-z0-9-]*$/.test(id) || ids.has(id) || offset <= previousOffset
            || text.indexOf(beforeText, offset + 1) !== -1) {
            throw new Error(`ScenePlan cue '${id}' requires a unique, ordered narration phrase and id.`);
        }
        ids.add(id);
        previousOffset = offset;
        return { raw, cue: { id, beforeText } };
    });
}

export function parsePlanCues(value: unknown, text: string): readonly PlanCue[] {
    return parseSpokenCues(value, text, ['id', 'beforeText', 'landmark', 'phase']).map(({ raw, cue }) => {
        if (raw.phase !== undefined && raw.phase !== 'start' && raw.phase !== 'complete') {
            throw new Error(`Invalid ScenePlan cue phase '${cue.id}'.`);
        }
        return { ...cue, landmark: requireText(raw.landmark, 'cue.landmark'),
            ...(raw.phase === undefined ? {} : { phase: raw.phase }) };
    });
}

export function parseConceptCues(value: unknown, text: string): readonly ConceptCue[] {
    return parseSpokenCues(value, text, ['id', 'beforeText']).map(({ cue }) => cue);
}

/** Empty effects clear preceding guidance; an arrangement persists until explicitly replaced. */
export function parseConceptPresentation<TArrangement, TTarget>(value: unknown, cues: readonly ConceptCue[] | undefined,
    parseArrangement: (value: unknown) => TArrangement, parseTarget: (value: unknown) => TTarget,
    capabilities: { cameraView?: boolean; orbit?: boolean } = {},
): readonly ConceptPresentation<TArrangement, TTarget>[] {
    if (!Array.isArray(value) || !value.length) throw new Error('Concept presentation requires segments.');
    const ids = new Set<string>();
    let previous = -2;
    return value.map((raw: unknown) => {
        if (!isRecord(raw)) throw new Error('Invalid concept presentation segment.');
        requireKnownFields(raw, ['id', 'afterCue', 'arrangement', 'pointerPlacement', 'textPanel', 'effects',
            ...(capabilities.cameraView ? ['cameraView'] : [])], 'concept presentation');
        const id = requireText(raw.id, 'presentation.id');
        const afterCue = raw.afterCue === undefined ? undefined : requireText(raw.afterCue, 'presentation.afterCue');
        const index = afterCue === undefined ? -1 : cues?.findIndex(cue => cue.id === afterCue) ?? -1;
        if (!/^[a-z][a-z0-9-]*$/.test(id) || ids.has(id) || (afterCue !== undefined && index < 0) || index <= previous) {
            throw new Error(`Concept presentation '${id}' requires unique ids and ordered cue references.`);
        }
        ids.add(id);
        previous = index;
        if (!Array.isArray(raw.effects)) throw new Error(`Concept presentation '${id}' requires effects.`);
        let orbits = 0;
        const effects = raw.effects.map((effect: unknown) => {
            if (!isRecord(effect)) throw new Error('Invalid concept effect.');
            if (effect.effectKind !== 'emphasis' && !(capabilities.orbit && effect.effectKind === 'orbit')) {
                throw new Error(capabilities.orbit ? 'Concept scenes support only emphasis and orbit effects.'
                    : 'Concept scenes support only emphasis effects.');
            }
            if (effect.effectKind === 'orbit' && ++orbits > 1) throw new Error('Invalid or conflicting orbit effect.');
            if (effect.effectKind === 'emphasis' && effect.caption !== undefined) {
                requireText(effect.caption, 'concept effect caption');
            }
            return parsePresentationEffect(effect, parseTarget);
        });
        return { id, effects, ...(afterCue === undefined ? {} : { afterCue }),
            ...(raw.pointerPlacement === undefined ? {} : { pointerPlacement: parsePointerPlacement(raw.pointerPlacement) }),
            ...(raw.textPanel === undefined ? {} : { textPanel: parseTextPanel(raw.textPanel) }),
            ...(raw.cameraView === undefined ? {} : { cameraView: requireText(raw.cameraView, 'presentation.cameraView') }),
            ...(raw.arrangement === undefined ? {} : { arrangement: parseArrangement(raw.arrangement) }) };
    });
}

/** Beats reveal segments cue by cue, whether or not the narration is audible. */
export function conceptPresentationAt<TArrangement, TTarget>(beat: ConceptBeat<TArrangement, TTarget>,
    playback: Pick<EpisodePlayback, 'cueIndex' | 'narrationCueReached'>) {
    const reached = playback.cueIndex ?? 0;
    let arrangement = beat.arrangement;
    let segment: ConceptPresentation<TArrangement, TTarget> | undefined;
    for (const entry of beat.presentation ?? []) {
        const cueIndex = entry.afterCue === undefined ? -1 : beat.cues?.findIndex(cue => cue.id === entry.afterCue) ?? -1;
        if (cueIndex >= reached && !isBeatStartCuePrepared(beat, playback, cueIndex)) break;
        segment = entry;
        arrangement = entry.arrangement ?? arrangement;
    }
    return { arrangement, segment };
}

/** Beat-start presentation is prepared while audio is still held before its first word. */
export function isBeatStartCuePrepared(
    beat: { explanation: string; spokenText?: string; cues?: readonly { beforeText: string }[] },
    playback: Pick<EpisodePlayback, 'narrationCueReached'>, cueIndex: number,
): boolean {
    const cue = beat.cues?.[0];
    return cueIndex === 0 && cue !== undefined && (playback.narrationCueReached ?? -1) >= 0
        && (beat.spokenText ?? beat.explanation).indexOf(cue.beforeText) === 0;
}

export function planAnchorIndex(replay: TraceReplay, anchor: { landmark: string; phase?: 'start' | 'complete' }): number {
    const index = replay.landmarks.findIndex(entry => entry.id === anchor.landmark);
    if (index < 0 || (anchor.phase === 'start' && index === 0)) {
        throw new Error(`ScenePlan anchor requires an ordered landmark with a preceding state: '${anchor.landmark}'.`);
    }
    return index - (anchor.phase === 'start' ? 1 : 0);
}

export function generateScenePlanDraft(replay: TraceReplay, title: string): ScenePlan {
    return parseTraceScenePlan({
        title, schemaVersion: 1, trace: replay.trace,
        beats: replay.landmarks.map((landmark, index) => ({
            id: `beat-${index}`, landmark: landmark.id, explanation: landmark.label, readingHoldMs: 4000,
        })),
    });
}
